/**
 * NavShell — hovedmenyen og assistentknappen (#153, PLAN.md steg 3).
 *
 * To varianter, valgt av vindusbredden (`useWideViewport`, 768 px):
 *   - `phone`  → en mørk pille, 278×64, nederst til venstre. Aktiv fane
 *                utvides med etiketten; de andre viser bare ikonet.
 *   - `tablet` → en loddrett mørk skinne til venstre med de samme fanene (D10).
 *
 * Fanene kommer fra rollen (`navTabs.ts`): barn og tenåringer får
 * Hjem · Oppgaver · Premier · Plan, voksne Hjem · Godkjenn · Familie · Plan.
 * Aktiv fane avgjøres av stien, så push-lenker lander med riktig fane tent.
 *
 * Assistenten er en lime rund knapp nederst til høyre på alle skall-skjermer
 * unntatt /assistent selv. Meg åpnes fra avataren i toppen (ScreenHeader).
 *
 * Fanene vises først når økten er kjent, så en voksen ikke ser barnemenyen
 * blinke forbi mens /api/auth/me svarer.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { Icon } from '../../components/primitives/Icon';
import { CountBadge } from '../../components/primitives/CountBadge';
import { useSession } from '../auth/useSession';
import { useIsAdult } from '../auth/useIsAdult';
import { usePendingApprovalCount } from '../approvals/useApprovalQueue';
import { activeNavTab, navTabsFor, showsAssistantFab, type NavTab } from './navTabs';

type Variant = 'tablet' | 'phone';

interface NavShellProps {
  variant: Variant;
}

/** Bunnen av pillen og knappen: 28 px som skissen, men aldri inn i hjemlinjen. */
const BOTTOM = 'bottom-[max(1.75rem,env(safe-area-inset-bottom))]';

function AssistantFab(): ReactNode {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  if (!showsAssistantFab(pathname)) return null;
  return (
    <Link
      to="/assistent"
      data-testid="nav-tab-chat"
      aria-label={t('nav.assistant')}
      className={`fixed right-5 ${BOTTOM} z-30 flex h-16 w-16 items-center justify-center rounded-full bg-fab text-on-fab shadow-fab`}
    >
      <Icon.NavAssistant size={26} />
    </Link>
  );
}

function TabLink({
  tab,
  active,
  variant,
  badge,
}: {
  tab: NavTab;
  active: boolean;
  variant: Variant;
  badge: ReactNode;
}): ReactNode {
  const { t } = useTranslation();
  const TabIcon = Icon[tab.icon];
  const label = t(tab.i18nKey);
  const shape =
    variant === 'phone'
      ? active
        ? 'h-12 gap-1.5 px-3.5 text-body-s'
        : 'h-12 w-12'
      : active
        ? 'h-16 w-16 flex-col gap-0.5 text-label'
        : 'h-12 w-12';
  return (
    <Link
      to={tab.to}
      data-testid={`nav-tab-${tab.key}`}
      aria-label={t('nav.tab_aria', { label })}
      aria-current={active ? 'page' : undefined}
      className={[
        'relative flex shrink-0 items-center justify-center rounded-pill transition-colors',
        shape,
        active ? 'bg-nav-ink font-extrabold text-nav' : 'text-nav-ink hover:bg-nav-ink/10',
      ].join(' ')}
    >
      <TabIcon size={20} />
      {active ? <span aria-hidden>{label}</span> : null}
      {badge}
    </Link>
  );
}

export function NavShell({ variant }: NavShellProps): ReactNode {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { isPending } = useSession();
  const isAdult = useIsAdult();
  const pendingApprovals = usePendingApprovalCount(isAdult);
  const tabs = isPending ? [] : navTabsFor(isAdult);
  const active = activeNavTab(pathname, isAdult);

  const items = tabs.map((tab) => (
    <TabLink
      key={tab.key}
      tab={tab}
      variant={variant}
      active={tab.key === active}
      badge={
        tab.key === 'approvals' ? (
          <CountBadge
            count={pendingApprovals}
            size="sm"
            label={t('nav.pendingApprovals', { count: pendingApprovals })}
            data-testid="nav-approval-badge"
            className="absolute -right-0.5 -top-0.5"
          />
        ) : null
      }
    />
  ));

  return (
    <>
      {variant === 'tablet' ? (
        <nav
          data-testid="nav-shell-tablet"
          aria-label={t('nav.main')}
          className="fixed bottom-4 left-4 top-4 z-30 flex w-20 flex-col items-center gap-2 rounded-pill bg-nav py-4 shadow-nav"
        >
          {items}
        </nav>
      ) : (
        <nav
          data-testid="nav-shell-phone"
          aria-label={t('nav.main')}
          // 112 px = 20 venstre + 64 knapp + 20 høyre + 8 luft: pillen krymper
          // heller enn å gli inn under assistentknappen på smale telefoner.
          className={`fixed left-5 ${BOTTOM} z-30 flex h-16 w-[min(278px,calc(100vw-112px))] items-center justify-between rounded-pill bg-nav px-2 shadow-nav`}
        >
          {items}
        </nav>
      )}
      <AssistantFab />
    </>
  );
}
